import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../api/block.api";
import { BlockPayload } from "../types/block.type";

export const BLOCK_QUERY_KEY = ["block"];

export const useBlocks = (page = 1, limit = 10, search = "") => {
	const query = useQuery({
		queryKey: [...BLOCK_QUERY_KEY, page, limit, search],
		queryFn: () => api.fetchBlockList(page, limit, search),
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
		placeholderData: keepPreviousData,
	});

	return {
		...query,
		blocks: query.data?.items ?? [],
		totalCount: query.data?.totalItems ?? 0,
		totalPages: query.data?.totalPages ?? 0,
	};
};

export const useBlockById = (id: string | number) => {
	const query = useQuery({
		queryKey: ["block", id],
		queryFn: () => api.fetchBlockById(id),
		enabled: Boolean(id),
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});

	return {
		...query,
		block: query.data ?? null,
	};
};

export const useCreateBlock = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: api.createBlock,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BLOCK_QUERY_KEY });
		},
	});

	return mutation;
};

export const useUpdateBlock = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: string | number;
			payload: BlockPayload;
		}) => api.updateBlock(id, payload),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: BLOCK_QUERY_KEY,
			});
		},
	});

	return mutation;
};

export const useDeleteBlock = () => {
	const queryClient = useQueryClient();
	const mutation = useMutation({
		mutationFn: (id: string | number) => api.deleteBlock(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: BLOCK_QUERY_KEY });
		},
	});

	return mutation;
};

export const useBlocksByDistrict = (
	districtId: string | number | undefined,
) => {
	return useQuery({
		queryKey: [...BLOCK_QUERY_KEY, "district", districtId],
		queryFn: () => api.fetchBlocksByDistrict(districtId!),
		enabled: !!districtId,
		staleTime: 5 * 60 * 1000,
		refetchOnWindowFocus: false,
	});
};
